import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBazi } from '../contexts/BaziContext';

const STORAGE_KEY = 'mpzy_onboarding_dismissed';

/** 检查是否首次访问或之前关掉了引导 */
function shouldShowOnboarding() {
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    return true;
  }
}

function markOnboardingDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {}
}

/** 步骤数据 */
function getSteps(isBaziLoaded) {
  return [
    {
      num: '一',
      icon: '📝',
      title: '输入出生信息',
      desc: '选择农历或公历，填出生年月日时和出生地，点击排盘',
      action: '去排盘',
      path: '/input',
      done: isBaziLoaded,
      highlight: true,
    },
    {
      num: '二',
      icon: '🔍',
      title: '查看你的命盘',
      desc: '确认八字四柱、日主五行、格局用神是否正确',
      action: '看命盘',
      path: '/result',
      done: isBaziLoaded,
    },
    {
      num: '三',
      icon: '✨',
      title: '使用专项功能',
      desc: '职业建议、大运流年、择偶匹配…选你感兴趣的深入探索',
      action: '往下浏览',
      path: null,
      done: false,
    },
    {
      num: '四',
      icon: '🦊',
      title: '咨询知知先生',
      desc: 'AI 命理顾问帮你解读命盘，事业财运感情都能问',
      action: '问知知',
      path: '/zhi',
      done: false,
    },
  ];
}

/**
 * 首次访问引导弹窗
 */
function GuideContent({ onDismiss, inModal = true }) {
  const navigate = useNavigate();
  const { isBaziLoaded } = useBazi();
  const steps = getSteps(isBaziLoaded);

  const handleAction = (step) => {
    if (step.path) {
      navigate(step.path);
    } else {
      // 往下浏览 → 关闭弹窗滚动到核心服务
      onDismiss();
      setTimeout(() => {
        const el = document.querySelector('[data-section="core-services"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  };

  return (
    <div className={`${inModal ? 'p-6 sm:p-8' : ''}`}>
      {/* 标题 */}
      <div className="text-center mb-6">
        <span className="text-3xl mb-2 block">📖</span>
        <h2 className="text-xl font-bold text-ink">欢迎来到命盘职引</h2>
        <p className="text-xs text-stone/40 mt-1">
          只需四步，开启你的命理探索之旅
        </p>
      </div>

      {/* 步骤列表 */}
      <div>
        {steps.map((step, i) => (
          <div key={step.num}>
            {i > 0 && (
              <div className="flex justify-center py-1">
                <div className="w-px h-3 bg-stone/15" />
              </div>
            )}

            <div
              className={`flex items-start gap-3 p-2 -mx-2 rounded-xl transition-all ${
                step.highlight && !step.done
                  ? 'bg-amber-50/80 ring-1 ring-amber-200/50'
                  : ''
              }`}
            >
              {/* 序号圆圈 */}
              <div
                className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  step.done
                    ? 'bg-green-100 text-green-600'
                    : step.highlight && !step.done
                    ? 'bg-amber-400 text-white shadow-sm animate-pulse'
                    : 'bg-stone/8 text-stone/50'
                }`}
              >
                {step.done ? '✓' : step.num}
              </div>

              {/* 内容 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-ink">
                    {step.icon} {step.title}
                  </span>
                  {step.done && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-600 font-medium">
                      已完成
                    </span>
                  )}
                  {step.highlight && !step.done && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                      开始
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone/45 mt-0.5 leading-relaxed">
                  {step.desc}
                </p>
              </div>

              {/* 操作按钮 */}
              {step.path && (
                <button
                  onClick={() => handleAction(step)}
                  className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full font-medium transition-all ${
                    step.done
                      ? 'text-green-600 bg-green-50 hover:bg-green-100'
                      : 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                  }`}
                >
                  {step.done ? '复习' : step.action} →
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 关闭按钮 */}
      {inModal && (
        <div className="mt-6 text-center">
          <button
            onClick={onDismiss}
            className="px-8 py-2.5 rounded-full bg-ink text-white text-sm font-medium hover:bg-ink/90 active:scale-95 transition-all"
          >
            知道了，开始使用
          </button>
          <p className="text-[10px] text-stone/25 mt-2">
            随时可点右下角 📖 按钮重新查看
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * 首次访问弹窗（Modal）
 */
export function OnboardingModal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shouldShowOnboarding()) {
      // 稍作延迟让页面先渲染
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    markOnboardingDismissed();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
        >
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto"
          >
            <GuideContent onDismiss={handleDismiss} inModal />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * 右下角悬浮 📖 按钮
 */
export function GuideFab() {
  const [showGuide, setShowGuide] = useState(false);
  const [pulseOnce, setPulseOnce] = useState(false);

  // 首次看到 FAB 时给一个微小的脉冲提示
  useEffect(() => {
    const t = setTimeout(() => setPulseOnce(true), 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* FAB 按钮 */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8, type: 'spring', stiffness: 300 }}
        onClick={() => setShowGuide(true)}
        className={`fixed z-[99] bottom-6 right-6 w-12 h-12 rounded-full bg-white shadow-lg border border-stone/15 flex items-center justify-center text-xl hover:shadow-xl hover:scale-105 active:scale-95 transition-all ${
          pulseOnce ? 'animate-none' : ''
        }`}
        title="使用指南"
      >
        <motion.span
          animate={pulseOnce ? { scale: [1, 1.3, 1] } : {}}
          transition={{ duration: 0.6 }}
        >
          📖
        </motion.span>
      </motion.button>

      {/* 浮层引导（小弹窗） */}
      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-end sm:justify-center p-4"
          >
            {/* 遮罩 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-ink/30 backdrop-blur-sm"
              onClick={() => setShowGuide(false)}
            />

            {/* 弹出卡片（靠右下方弹出） */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full max-h-[80vh] overflow-y-auto sm:absolute sm:bottom-20 sm:right-6"
            >
              {/* 关闭按钮 */}
              <button
                onClick={() => setShowGuide(false)}
                className="absolute top-3 right-3 w-7 h-7 rounded-full bg-stone/5 flex items-center justify-center text-stone/40 hover:bg-stone/10 hover:text-stone/60 transition-colors z-10"
              >
                ✕
              </button>
              <GuideContent
                onDismiss={() => setShowGuide(false)}
                inModal={false}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default OnboardingModal;
